import { Box, IconButton, Tooltip } from '@mui/material';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { type Layout, ReactGridLayout, useContainerWidth } from 'react-grid-layout';
import RefreshIcon from '@mui/icons-material/Refresh';

type GridConfig = {
    /** Number of columns in the grid (default: 12) */
    cols: number;
    /** Height of a single row in pixels (default: 150) */
    rowHeight: number;
    /** [horizontal, vertical] margin between items in pixels (default: [10, 10]) */
    margin: readonly [number, number];
    /** [horizontal, vertical] padding inside the container (default: null, uses margin) */
    containerPadding: readonly [number, number] | null;
    /** Maximum number of rows (default: Infinity) */
    maxRows: number;
};
type ReactGridContainerProps = {
    localStorageName: string;
    layoutProps: Layout;
    gridconfig?: Partial<GridConfig>;
    dragConfig?: {
        handle?: string;
        cancel?: string;
        enabled?: boolean;
        bounded?: boolean;
        threshold?: number;
    };
    currentXIndexCard?: React.MutableRefObject<number>;
    currentYIndexCard?: React.MutableRefObject<number>;
    initialXIndexCard?: number;
    showResetButton?: boolean;
};

/**
 *
 * exemple layout
 * const layout: Layout = [
 *      { i: 'a', x: 0, y: 0, w: 1, h: 1, isResizable: true, resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] },
 *      { i: 'd', x: 1, y: 3, w: 1, h: 4, isResizable: true, resizeHandles: ['s'] },
 *      { i: 'b', x: 2, y: 1, w: 1, h: 2, isResizable: false},
 *      { i: 'c', x: 3, y: 0, w: 1, h: 2 },
 *  ];
 * @param param0
 */
const ReactGridContainer: React.FunctionComponent<PropsWithChildren<ReactGridContainerProps>> =
    function ({
        localStorageName,
        layoutProps,
        gridconfig = { cols: 6, rowHeight: 40 },
        dragConfig,
        currentXIndexCard,
        initialXIndexCard = -1,
        currentYIndexCard,
        showResetButton = true,
        children,
    }) {
        const { width, containerRef, mounted } = useContainerWidth();
        const [layout, setLayout] = useState<Layout>();

        // Check if layout is stored in the local storage on mount
        useEffect(() => {
            if (layoutProps && layoutProps.length > 0) {
                const storedLayout = localStorage.getItem(localStorageName);

                if (storedLayout !== null) {
                    // Merge stored positions with current layoutProps to pick up new properties
                    // (e.g. resizeHandles added after the layout was first saved, or new items added)
                    const stored: Layout = JSON.parse(storedLayout);
                    const merged = stored.map((item) => {
                        const initial = layoutProps.find((l) => l.i === item.i);
                        return initial
                            ? {
                                  ...item,
                                  resizeHandles: initial.resizeHandles,
                                  isResizable: initial.isResizable,
                              }
                            : item;
                    });
                    const missingItems = layoutProps.filter(
                        (l) => !stored.some((s) => s.i === l.i),
                    );
                    setLayout([...merged, ...missingItems]);
                } else if (localStorage.getItem(localStorageName) !== JSON.stringify(layoutProps)) {
                    setLayout(layoutProps);
                    localStorage.setItem(localStorageName, JSON.stringify(layoutProps));
                }
            }
            if (currentXIndexCard && currentXIndexCard.current) {
                // eslint-disable-next-line no-param-reassign
                currentXIndexCard.current = initialXIndexCard;
            }

            if (currentYIndexCard && currentYIndexCard.current) {
                // eslint-disable-next-line no-param-reassign
                currentYIndexCard.current = 0;
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [localStorageName, layoutProps]);

        // On change store the new layout in localStorage and track in state
        const handleLayoutChange = (layoutParam: Layout) => {
            if (layoutParam) {
                if (localStorage.getItem(localStorageName) !== JSON.stringify(layoutParam)) {
                    localStorage.setItem(localStorageName, JSON.stringify(layoutParam));
                }
                setLayout(layoutParam);
            }
        };

        const handleResetLayout = useCallback(() => {
            if (layoutProps && layoutProps.length > 0) {
                localStorage.setItem(localStorageName, JSON.stringify(layoutProps));
                setLayout([...layoutProps]);
            }
        }, [layoutProps, localStorageName]);

        return (
            <Box
                sx={{ width: '100%', position: 'relative', padding: '16px 0px' }}
                ref={containerRef}
            >
                {showResetButton && (
                    <Tooltip title="Réinitialiser">
                        <IconButton
                            onClick={handleResetLayout}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                zIndex: 10,
                            }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {mounted && layout && (
                    <ReactGridLayout
                        layout={layout}
                        width={width}
                        gridConfig={gridconfig}
                        dragConfig={dragConfig}
                        onLayoutChange={handleLayoutChange}
                    >
                        {children}
                    </ReactGridLayout>
                )}
            </Box>
        );
    };

export default ReactGridContainer;
